import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { CancelToken, isCancel } from 'axios';
import Waypoint from 'react-waypoint';

import BackgroundImage from '../../BackgroundImage';
import Icon from '../../Img/Icon';
import PlayerChip from '../PlayerChip';
import { getPlayerId, getPlayerName } from '../playerShape';
import { searchAddPlayers } from '../../../api/savedListsApi';
import { getApiAxiosInstance } from '../../../utils/network';
import { getRatingDetails, setRatingChoice } from '../../../utils';

import styles from './multiPlayerSearch.scss';

// The Add-Player wizard (TMS-2104 sub-task C) is this component's only consumer and the
// ticket pins these exact literals as the data-testid contract the web verifier selects on,
// so the prefix is fixed here rather than forwarded from the caller (same posture as the
// sibling SaveToListModal). selfrate emits lowercase data-testid.
const TESTID_PREFIX = 'add-player-search';
// 2 (not 3) so short player names like "QA" are searchable, matching global search's low threshold.
// The query is trimmed first, so leading/trailing whitespace never counts toward the minimum.
const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;
// The TMS-2165 endpoint owns the real default; we send an explicit size so a short final
// page is a reliable end-of-results signal for the infinite scroll.
const PAGE_SIZE = 20;
const DEFAULT_AVATAR = '/img/v2/icons/icon-avatar-player.svg';
const SEARCH_PLACEHOLDER = 'Search players';
const ADD_ANOTHER_PLACEHOLDER = 'Add another…';
const SHORT_QUERY_MESSAGE = 'Enter at least 2 characters to search.';
const NO_RESULTS_SUBTITLE = 'Check spelling, or try a different search';
const ERROR_MESSAGE = 'Something went wrong. Please try again.';
const SAVED_TOOLTIP = 'Saved on an existing list';
const LOADING_MESSAGE = 'Loading...';
const META_SEPARATOR = ' • ';

const normalizePlayer = (item) => ({
    // Carry the raw rating projection through (singlesUtr/singlesUtrDisplay/ratingStatus*/myUtr*/
    // utrRange/historicRatings) so setRatingChoice/getRatingDetails can render the UTR the same way
    // partner search does; only the identity/display fields below are normalized on top.
    ...item,
    playerId: item.playerId ?? item.id,
    // The search endpoint returns the name split as firstName/lastName (no displayName/name field),
    // so fall back to composing it from those before giving up on a blank name.
    displayName: item.displayName ?? item.name ?? [item.firstName, item.lastName].filter(Boolean).join(' '),
    gender: item.gender ?? '',
    location: typeof item.location === 'string' ? item.location : (item.location?.display ?? ''),
    isSaved: !!item.isSaved,
    playerProfileImages: item.playerProfileImages ?? item.playerImages ?? null,
});

const ResultRow = ({ player, rowKey, isSelected, onSelect }) => {
    const { displayName, gender, location, isSaved, playerProfileImages } = player;
    // Pin the row to the Verified Singles UTR, regardless of the member's default rating choice, so it
    // matches partner search's display without the doubles/my-UTR flip. Must be the getRatingDetails
    // dispatch value 'VerifiedSinglesUTR' (not the profile-preference RatingChoices.UTR_RATING): the
    // rated path treats both alike, but the unrated/inactive historic-singles fallback is gated on
    // SINGLES_HISTORIC_CHOICES, which excludes 'UTRRating' — so that value silently hides the historic
    // rating the search API sends for those players.
    const ratingObject = setRatingChoice({ player });
    ratingObject.ratingChoice = 'VerifiedSinglesUTR';
    const rating = getRatingDetails(ratingObject, true, false, false, false)?.value;
    const rowClass = `${styles.resultRow} ${isSelected ? styles.selected : ''}`;
    const meta = [gender, rating, location].filter(Boolean).join(META_SEPARATOR);
    const handleKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onSelect(player);
        }
    };
    return (
        <div
            className={rowClass}
            role="button"
            tabIndex={0}
            onClick={() => onSelect(player)}
            onKeyDown={handleKeyDown}
            data-testid={`${TESTID_PREFIX}.result-${rowKey}`}
        >
            <div className={styles.avatar} data-testid={`${TESTID_PREFIX}.result-${rowKey}.avatar`}>
                <BackgroundImage
                    baseUrl={'/v1/player/'}
                    defaultImage={DEFAULT_AVATAR}
                    images={playerProfileImages}
                    imageType={'icon'}
                    parentStylesClass={styles.avatarImage}
                />
            </div>
            <div className={styles.resultInfo}>
                <div className={styles.resultName} data-testid={`${TESTID_PREFIX}.result-${rowKey}.name`}>
                    {displayName}
                </div>
                <div className={styles.resultMeta} data-testid={`${TESTID_PREFIX}.result-${rowKey}.meta`}>
                    {meta}
                </div>
            </div>
            {isSaved && (
                <span
                    className={styles.savedMarker}
                    title={SAVED_TOOLTIP}
                    data-testid={`${TESTID_PREFIX}.result-${rowKey}.saved`}
                >
                    <Icon src="/img/v3/icons/icon-check-seal.svg" size={12} tone="black" />
                </span>
            )}
        </div>
    );
};

ResultRow.propTypes = {
    player: PropTypes.instanceOf(Object).isRequired,
    rowKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    isSelected: PropTypes.bool.isRequired,
    onSelect: PropTypes.func.isRequired,
};

const MultiPlayerSearch = ({ selectedPlayers, onAdd, onRemove }) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [isEnd, setIsEnd] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [showShortQueryError, setShowShortQueryError] = useState(false);

    const debounceRef = useRef(null);
    const cancelRef = useRef(null);
    // Monotonic request id: only the response whose id still matches the latest request may
    // update state, so a stale query's or stale page's late response is ignored even if its
    // cancellation lost the race.
    const requestSeqRef = useRef(0);
    const inputRef = useRef(null);

    // Cancel any pending debounce and in-flight request on unmount, and advance the request id
    // so a late response can never call setState on an unmounted component.
    useEffect(() => () => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        if (cancelRef.current) {
            cancelRef.current.cancel();
        }
        requestSeqRef.current += 1;
    }, []);

    const selectedIds = new Set(selectedPlayers.map((player) => String(getPlayerId(player))));
    const isSelected = (playerId) => selectedIds.has(String(playerId));

    const invalidatePending = () => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }
        if (cancelRef.current) {
            cancelRef.current.cancel();
            cancelRef.current = null;
        }
        requestSeqRef.current += 1;
    };

    const runSearch = (trimmedQuery, pageToFetch, append) => {
        invalidatePending();
        const seq = requestSeqRef.current;
        const source = CancelToken.source();
        cancelRef.current = source;
        const axiosInstance = getApiAxiosInstance();
        searchAddPlayers(axiosInstance, {
            query: trimmedQuery,
            page: pageToFetch,
            size: PAGE_SIZE,
            cancelToken: source.token,
        })
            .then((response) => {
                if (seq !== requestSeqRef.current) {
                    return;
                }
                cancelRef.current = null;
                const rawItems = response?.data?.items ?? response?.data;
                const items = (Array.isArray(rawItems) ? rawItems : []).map(normalizePlayer);
                setResults((prev) => (append ? [...prev, ...items] : items));
                setIsEnd(items.length < PAGE_SIZE);
                setPage(pageToFetch);
                setIsLoading(false);
                setHasSearched(true);
                setHasError(false);
            })
            .catch((error) => {
                if (isCancel(error) || seq !== requestSeqRef.current) {
                    return;
                }
                cancelRef.current = null;
                if (!append) {
                    setResults([]);
                }
                setHasError(true);
                setIsEnd(true);
                setIsLoading(false);
                setHasSearched(true);
            });
    };

    const handleInputChange = (event) => {
        const { value } = event.target;
        setQuery(value);
        setShowShortQueryError(false);
        setHasError(false);
        invalidatePending();

        const trimmedQuery = value.trim();
        if (trimmedQuery.length < MIN_QUERY_LENGTH) {
            setResults([]);
            setIsLoading(false);
            setHasSearched(false);
            setPage(1);
            setIsEnd(false);
            return;
        }

        setResults([]);
        setPage(1);
        setIsEnd(false);
        setIsLoading(true);
        debounceRef.current = setTimeout(() => {
            runSearch(trimmedQuery, 1, false);
        }, DEBOUNCE_MS);
    };

    const handleKeyDown = (event) => {
        if (event.key !== 'Enter') {
            return;
        }
        event.preventDefault();
        const trimmedQuery = query.trim();
        if (trimmedQuery.length > 0 && trimmedQuery.length < MIN_QUERY_LENGTH) {
            setShowShortQueryError(true);
        }
    };

    const handleLoadMore = () => {
        if (isLoading || isEnd) {
            return;
        }
        const trimmedQuery = query.trim();
        if (trimmedQuery.length < MIN_QUERY_LENGTH) {
            return;
        }
        setIsLoading(true);
        runSearch(trimmedQuery, page + 1, true);
    };

    const resetSearch = () => {
        invalidatePending();
        setQuery('');
        setResults([]);
        setPage(1);
        setIsEnd(false);
        setIsLoading(false);
        setHasSearched(false);
        setHasError(false);
        setShowShortQueryError(false);
    };

    const focusInput = () => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    const handleRowSelect = (player) => {
        if (isSelected(player.playerId)) {
            return;
        }
        onAdd(player);
        // Clear the query + collapse the dropdown so the field returns to the "Add another…" state,
        // and keep focus so the member can immediately search the next player.
        resetSearch();
        focusInput();
    };

    const trimmedQuery = query.trim();
    const canSearch = trimmedQuery.length >= MIN_QUERY_LENGTH;
    const showDropdown = canSearch && (isLoading || hasSearched);
    const hasResults = results.length > 0;
    const showError = showDropdown && !isLoading && hasError && !hasResults;
    const showNoResults = showDropdown && !isLoading && hasSearched && !hasResults && !hasError;
    const matchingLabel = `${results.length} Matching Player${results.length === 1 ? '' : 's'}`;

    return (
        <div className={styles.container} data-testid={TESTID_PREFIX}>
            <div
                className={styles.field}
                onClick={focusInput}
                data-testid={`${TESTID_PREFIX}.field`}
            >
                {selectedPlayers.map((player, index) => {
                    const chipKey = getPlayerId(player) ?? index;
                    return (
                        <PlayerChip
                            key={chipKey}
                            data-testid={`${TESTID_PREFIX}.chip-${chipKey}`}
                            name={getPlayerName(player)}
                            onRemove={() => onRemove(getPlayerId(player))}
                        />
                    );
                })}
                <div className={styles.inputRow}>
                    <i className={`fas fa-search ${styles.searchIcon}`} aria-hidden="true" />
                    <input
                        ref={inputRef}
                        type="text"
                        className={styles.input}
                        value={query}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        placeholder={selectedPlayers.length > 0 ? ADD_ANOTHER_PLACEHOLDER : SEARCH_PLACEHOLDER}
                        data-testid={`${TESTID_PREFIX}.input`}
                    />
                </div>
            </div>

            {showShortQueryError && (
                <div className={styles.shortQueryError} data-testid={`${TESTID_PREFIX}.short-query-error`}>
                    {SHORT_QUERY_MESSAGE}
                </div>
            )}

            {showDropdown && (
                <div className={styles.results} data-testid={`${TESTID_PREFIX}.results`}>
                    {hasResults && (
                        <>
                            <div className={styles.resultsCount} data-testid={`${TESTID_PREFIX}.results-count`}>
                                {matchingLabel}
                            </div>
                            {results.map((player, index) => {
                                const rowKey = getPlayerId(player) ?? index;
                                return (
                                    <ResultRow
                                        key={rowKey}
                                        player={player}
                                        rowKey={rowKey}
                                        isSelected={isSelected(player.playerId)}
                                        onSelect={handleRowSelect}
                                    />
                                );
                            })}
                            {!isEnd && !isLoading && (
                                <Waypoint onEnter={handleLoadMore} bottomOffset={'-40px'} />
                            )}
                        </>
                    )}
                    {isLoading && (
                        <div className={styles.loading} data-testid={`${TESTID_PREFIX}.loading`}>
                            {LOADING_MESSAGE}
                        </div>
                    )}
                    {showNoResults && (
                        <div className={styles.noResults} data-testid={`${TESTID_PREFIX}.no-results`}>
                            <span
                                className={styles.noResultsTitle}
                                data-testid={`${TESTID_PREFIX}.no-results.title`}
                            >
                                {`No players match "${trimmedQuery}"`}
                            </span>
                            <span
                                className={styles.noResultsSubtitle}
                                data-testid={`${TESTID_PREFIX}.no-results.subtitle`}
                            >
                                {NO_RESULTS_SUBTITLE}
                            </span>
                        </div>
                    )}
                    {showError && (
                        <div className={styles.noResults} data-testid={`${TESTID_PREFIX}.error`}>
                            {ERROR_MESSAGE}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

MultiPlayerSearch.propTypes = {
    selectedPlayers: PropTypes.arrayOf(PropTypes.shape({
        playerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        displayName: PropTypes.string,
    })),
    onAdd: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
};

MultiPlayerSearch.defaultProps = {
    selectedPlayers: [],
};

export default MultiPlayerSearch;
